import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import axios from "axios";
import { AddProperty, PropertiesResponse, Property } from "./types/property";
import toast from "react-hot-toast";

type PaymentStatusResponse = {
  status: number;
  message: string;
  data: {
    [month: string]: string; 
  };
};

interface RentalAgreement {
  id?: number;
  propertyId: number;
  rentAmount: number;
  startDate: string;
  endDate: string | null;
}

  export const UnoccupiedProperty = (params: { active: boolean, page?: number, size?: number }) => useQuery({
    queryKey: ["unoccupiedProperties", params],
    queryFn: async ({ queryKey }) => {
      try {
        const response = await axios.get(`/api/v1/getActiveInActiveUnOccupied`, {
          params: queryKey[1],
        });
       // console.log("Unoccupied properties response:", response.data);
        //return response.data as PropertiesResponse;

        // console.log(" Full API Response:", response.data);
        // console.log("Extracted data:", response.data?.data);
        // console.log("Extracted UNOCCUPIED:", response.data?.data?.UNOCCUPIED);
        const extractedData = response.data?.data?.UNOCCUPIED ?? [];  // Explicitly storing extracted data
       // console.log("🔹 Returning UNOCCUPIED:", extractedData);
        return extractedData;
      } catch (error) {
        console.error("Error fetching unoccupied properties:", error);
        return [];
      }
    },
    staleTime: 30000,
    retry: false,
  });

// GET all properties
export function useProperties() {

  const defaultProperty: AddProperty = {
    propertyName: "Default Name",
    id: undefined,
    propertyAddress: "",
    propertyType: "",
    propertyRent: 0,
   // propertyStatus: "",
     propertyStatus: "UNOCCUPIED",
    noOfRooms: 0,
    propertyDepositAmount: 0,
    propertySize: 0,
    isActive: true
  };
  const queryClient = useQueryClient();
const toastId = "expense-toast";

const Property = (params: { active: boolean | null, delete: boolean, page?: number, size?: number }) => useQuery({
  queryKey: ["properties", params],
  queryFn: async ({ queryKey }) => {
    try {
      const { active, delete: deleteParam, page, size } = queryKey[1] as any;
      
    
      const requestParams: any = {
        delete: deleteParam,
      };
      
    
      if (active !== null) {
        requestParams.active = active;
      }
      

      if (page !== undefined) requestParams.page = page;
      if (size !== undefined) requestParams.size = size;
      
      const response = await axios.get(`/api/v1/getActiveInActiveProperty`, {
        params: requestParams,
      });
      return response.data as PropertiesResponse;
    } catch (error) {
      console.error("Error fetching properties:", error);
  
    }
  },
  staleTime: 30000,
  retry: false,
});



  const PropertyHistory = (id: any, year: number) => useQuery({
    queryKey: ["history", id, year], 
    queryFn: async () => {
      try {

        const response = await axios.get(`/api/v1/properties/${id}/payments?year=${year}`); 
        return response.data as PaymentStatusResponse;
      } catch (error) {
        console.error("Error fetching payment history:", error);
        throw error;
      }
    },
    enabled: !!id,
    staleTime: 300000,
    retry: false,
  });



  const addProperty = useMutation({
    mutationFn: async (newProperty: Omit<AddProperty, 'id'>) => {
      const PropertyData: AddProperty = { ...defaultProperty, ...newProperty };

      const formData = new FormData();

      Object.keys(PropertyData).forEach((key) => {
        const value = PropertyData[key as keyof AddProperty];
        if (value !== undefined) {
          if (value instanceof File) {
            formData.append(key, value);
          } else {
            formData.append(key, value.toString());
          }
        }
      });

  
      const response = await axios.post(`/api/v1/addProperty`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data; 
    },
    onError: (error) => {
      console.error("Error adding property:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to add Property, please try again"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] }); 
       toast.success("Property has been successfully added.", { id: toastId });
    },
  });

  const getPropertyById = useMutation({
    mutationFn: async (propertyId: string) => {
      try {
        const response = await axios.get(`/properties/${propertyId}`);
        return response.data;
      } catch (error) {
        console.error("Error fetching property by ID:", error);
        throw error;
      }
    },
  });


  const updateProperty = useMutation({
    mutationFn: async ({ propertyId, updatedProperty }: { propertyId: string; updatedProperty: Omit<Property, 'id'> }) => {
      const formData = new FormData();
  
      const isFile = (value: any): value is File => value instanceof File;
  
      Object.entries(updatedProperty).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          if (isFile(value)) {
            formData.append(key, value);
            
             formData.append("document", value);
          } else if (typeof value === "boolean" || typeof value === "number") {
            formData.append(key, value.toString());
          } else if (typeof value === "string") {
            formData.append(key, value);
          }
        }
      });
   
      const response = await axios.put(`/api/v1/propertyEdit/${propertyId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });
   
      return response.data;
    },
      onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] }); 
       toast.success("Property has been updated successfully", { id: toastId });
    },
  });
  
  // DELETE property
  const deleteProperty = useMutation({
    mutationFn: async (id: string) => {
      const response = await axios.put(`/api/v1/tempDeleteProperty/${id}`);
      return response.data;
    },
    onError: (error) => {
      console.error("Error deleting property:", error);
      toast.error(
        error.message
          ? `${error.message}`
          : "Sorry, Failed to delete Property, please try again"
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["properties"] }); 
        toast.success("Property has been successfully deleted.", { id: toastId });
    },
  });


    // GET rental agreements for a property
  const RentalAgreements = (propertyId: number) => useQuery({
    queryKey: ["rentalAgreements", propertyId],
    queryFn: async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`/api/v1/getAllRentalAgreement/${propertyId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.data) {
          return response.data.data as RentalAgreement[];
        }
        return [];
      } catch (error) {
        console.error("Error fetching rental agreements:", error);
        toast.error("Failed to load rental agreements");
        return [];
      }
    },
    enabled: !!propertyId,
    staleTime: 30000,
    retry: false,
  });

  // ADD rental agreement
  const addRentalAgreement = useMutation({
    mutationFn: async (agreementData: Omit<RentalAgreement, 'id'>) => {
      const token = localStorage.getItem("token");
      const response = await axios.post('/api/v1/addRentalAgreement', agreementData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      return response.data;
    },
    onError: (error: any) => {
      console.error("Error adding rental agreement:", error);
      toast.error(error.response?.data?.message || "Failed to add rental agreement");
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["rentalAgreements"] });
      toast.success("Rental agreement added successfully");
      return data.data?.id; // Return the ID of the created agreement
    },
  });

  // UPDATE rental agreement
  const updateRentalAgreement = useMutation({
    mutationFn: async (agreementData: RentalAgreement) => {
      const token = localStorage.getItem("token");
      const response = await axios.patch('/api/v1/updateRentalAgreement', agreementData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      return response.data;
    },
    onError: (error: any) => {
      console.error("Error updating rental agreement:", error);
      toast.error(error.response?.data?.message || "Failed to update rental agreement");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rentalAgreements"] });
      toast.success("Rental agreement updated successfully");
    },
  });

  // DELETE rental agreement
  const deleteRentalAgreement = useMutation({
    mutationFn: async (agreementId: number) => {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`/api/v1/delete/${agreementId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      return response.data;
    },
    onError: (error: any) => {
      console.error("Error deleting rental agreement:", error);
      toast.error(error.response?.data?.message || "Failed to delete rental agreement");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rentalAgreements"] });
      toast.success("Rental agreement deleted successfully");
    },
  });

  return { Property, PropertyHistory, addProperty, queryClient, getPropertyById, updateProperty, deleteProperty ,
     addRentalAgreement,
    updateRentalAgreement,
    deleteRentalAgreement,
    RentalAgreements
  };
}
